<template>
  <div :class="$style.container">
    <!-- ------ -->
    <!-- HEADER -->
    <!-- ------ -->

    <!-- TOP NAVIGATION HEADER (PROFILE) -->
    <!-- ------------------------------- -->
    <div v-if="showMainNavigation" :class="$style.headerProfile">
      <slot name="header"></slot>
    </div>

    <!-- SUB NAVIGATION HEADER -->
    <!-- --------------------- -->

    <div v-else :class="$style.drawerHead">
      <!-- BACK BUTTON -->
      <div :class="$style.backButton" @click="handleBackNavigation">
        <NsIcon
          :class="$style.backIcon"
          icon-name="icon-narrow-angle-left-24"
        />
      </div>

      <!-- HEADER TITLE -->
      <div :class="$style.drawerTitle">
        {{ activeNavigation.title || activeNavigation.name }}
      </div>

      <!-- CLOSE BUTTON -->
      <div :class="$style.closeButton" @click="handleCloseClick">
        <NsIcon
          :class="$style.closeIcon"
          icon-name="icon-close-24"
          size="custom"
        />
      </div>
    </div>

    <!-- ---------- -->
    <!-- NAVIGATION -->
    <!-- ---------- -->

    <nav role="navigation">
      <!-- HOME LINK -->
      <!-- --------- -->
      <div v-if="showMainNavigation">
        <a :class="$style.navItem" :href="localHomeUrl">
          <span :class="$style.navTitle" v-html="translations.home" />
        </a>
      </div>

      <!-- DYNAMIC LINKS -->
      <!-- ------------- -->
      <template v-if="showSubNavigation">
        <div
          v-for="(navItem, navIndex) in activeNavigation.subnav"
          :key="navItem.title || navItem.name"
          :class="$style.navItem"
        >
          <a
            v-if="allowSubNavigation(navItem)"
            :class="$style.navItem"
            href="javascript:void(0)"
            @click="selectSubNavigation(navIndex)"
          >
            <span :class="$style.navTitle">
              {{ navItem.title || navItem.name }}
            </span>
            <NsIcon :class="$style.navIcon" icon-name="icon-direction-mc" />
          </a>
          <a v-else :class="$style.navItem" :href="navItem.href">
            <span :class="$style.navTitle">
              {{ navItem.title || navItem.name }}
            </span>
          </a>
        </div>

        <!-- SUBNAV SEE ALL LINK -->
        <div v-if="showSeeAllLink">
          <a :class="$style.navItem" :href="activeNavigation.href">
            <span :class="$style.navTitle">{{ activeCategoryLinkText }}</span>
          </a>
        </div>
      </template>

      <!-- JOIN US & LEGAL LINKS & LINK LIST -->
      <!-- --------------------------------- -->
      <template v-if="showMainNavigation">
        <!-- JOIN US LINK -->
        <div v-if="joinUs.label && joinUs.link">
          <a :class="$style.navItem" :href="joinUs.link">
            <span :class="$style.navTitle">{{ joinUs.label }}</span>
          </a>
        </div>

        <!-- LEGAL LINKS -->
        <template>
          <div v-for="link in limitedLegalLinks" :key="link.name">
            <a :class="$style.navItem" :href="link.href">
              <span :class="$style.navTitle">{{ link.name }}</span>
            </a>
          </div>
        </template>
      </template>

      <!-- TOPNAV CUSTOM COMPONENT -->
      <!-- ----------------------- -->
      <div v-if="activeNavigationCustomComponent">
        <NsDynamicHtml :template="activeNavigationCustomComponent" />
      </div>
    </nav>

    <div v-if="showMainNavigation" :class="$style.secondaryActions">
      <!-- REGION DROPDOWN -->
      <!-- --------------- -->
      <div :class="$style.regionSelector">
        <NsMinimalHeaderRegionSelector
          :href-override="homeUrl"
          :region="region"
          :region-selector-path="regionSelectorPath"
          :translations="translations"
        />
      </div>
      <div v-if="isLoggedIn" :class="$style.signOutLink" @click="signOut">
        <span>{{ translations.signOutLabel }}</span>
      </div>
      <div v-if="!isLoggedIn" :class="$style.signOutLink">
        <span @click="signIn">{{ translations.signInLabel }}</span>
        <span :class="$style.slash">/</span>
        <span @click="signUp">{{ translations.signUpLabel }}</span>
      </div>
    </div>

    <!-- ------ -->
    <!-- FOOTER -->
    <!-- ------ -->

    <div v-if="showFooterNav" :class="$style.footerNav">
      <!-- FOOTER SIGNUP -->
      <div :class="$style.footerNavHead">
        <button
          class="ghost-inverse fluid"
          @click="handleFooterSignupClick"
          v-html="translations.sendSignupInvite"
        />
      </div>

      <!-- FOOTER NAVIGATION -->
      <div :class="$style.footerNavBody">
        <NsFooterNav :navigation="footerNavigation" expandbreakpoint="none" />
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import {
  events,
  MarketSiteRegions,
  RunConfigService,
  SitesLocalStorageService,
  SitesSessionStorageService,
  StringService,
  util
} from "@nuskin/ns-util";
import { AccountManager, UserService } from "@nuskin/ns-account";
import { TopNavService } from "@nuskin/ns-product";
import { NsIcon } from "@nuskin/design-components";
import { isString } from "@nuskin/ns-common-lib";

import NsMinimalHeaderRegionSelector from "./NsMinimalHeaderRegionSelector.vue";

import NsDynamicHtml from "../NsDynamicHtml.vue";
import NsFooterNav from "../NsFooterNav.vue";
import vueEventBus from "../EventBus";

const AUTO_REDIRECT_KEY = "autoRedirectGlobalLanding";
const CURRENT_PAGE_KEY = "currentPage";
const REGIONS_ALLOWED_KEY = "regionsAllowed";
const HREF_OVERRIDE_KEY = "hrefOverride";

const TOPNAV_PRODUCT_LINE = "nsTopnavProductLine";
const TOPNAV_LIST_OF_LINKS = "nsNavListOfLinks";
const TOPNAV_PRODUCT_HIGHLIGHT = "nsTopnavProductHighlight";
const TOPNAV_GIVING_BACK = "nsTopnavGivingBack";
const TOPNAV_CONTENT_TYPES = [
  TOPNAV_PRODUCT_LINE,
  TOPNAV_LIST_OF_LINKS,
  TOPNAV_PRODUCT_HIGHLIGHT,
  TOPNAV_GIVING_BACK
];
const SUBNAV_HIDDEN_CONTENT_TYPES = [TOPNAV_PRODUCT_LINE, TOPNAV_GIVING_BACK];

export default {
  name: "NsMinimalHeaderDrawer",
  components: {
    NsIcon,
    NsDynamicHtml,
    NsFooterNav,
    NsMinimalHeaderRegionSelector
  },
  props: {
    /**
     * Navigation for the footer
     */
    footerNavigation: {
      type: Array,
      default: () => []
    },
    /**
     * Allows you to set where the "select other region" redirects too
     */
    regionSelectorPath: {
      type: String,
      default: "/content/nuskin.html#region-selection"
    },
    /**
     * Home url
     */
    homeUrl: {
      type: String,
      default: ""
    },
    /**
     * If set, this will override the page redirect on the region selector page
     */
    hrefOverride: {
      type: String,
      default: ""
    },
    /**
     * Join us label and link
     */
    joinUs: {
      type: Object,
      default: () => ({
        label: "",
        link: ""
      })
    },
    /**
     * Array of links to display in the top utility bar: left
     * Objects in array require 'href' and 'name'
     */
    legalLinks: {
      type: Array,
      default: () => []
    },
    /**
     * Main navigation. Default is null in order to load by service if nothing
     * is passed from parent
     */
    navigation: {
      type: Array,
      default: null
    },
    /**
     * The region
     */
    region: {
      type: Object,
      default: null
    },
    /**
     * If set, this will only show regions according to what is in the array
     * [ "en_US", "es_US" ]
     */
    regionsAllowed: {
      type: Array,
      default: () => []
    },
    /**
     * Signup url
     */
    signupHref: {
      type: String,
      default: ""
    },
    /**
     * Translations
     */
    translations: {
      type: Object,
      default: () => ({
        selectOther: "Select Another Region",
        sendSignupInvite: "Join Nu Skin",
        home: "Home"
      })
    }
  },
  data() {
    return {
      activeNavigationCustomComponent: "",
      allCategoryText: "",
      currentPage: "",
      navigationFromService: [],
      navigationStack: [],
      regionFromService: {},
      regionSelectorOpen: false,
      isLoggedIn: UserService.isLoggedIn()
    };
  },
  computed: {
    runConfig() {
      return this.$NsAppService.runConfig || {};
    },
    activeCategoryLinkText() {
      if (this.showMainNavigation) {
        return "";
      }
      return this.allCategoryText.replace(
        "%categoryName%",
        this.activeNavigation.title || this.activeNavigation.name
      );
    },
    activeNavigation() {
      const activeNavigation = { subnav: this.localNavigation };
      if (this.showMainNavigation) {
        return activeNavigation;
      } else {
        return this.getActiveNavigation(activeNavigation);
      }
    },
    // limit links to 3;
    limitedLegalLinks() {
      return (
        this.legalLinks && this.legalLinks.filter(link => link.name).slice(0, 3)
      );
    },
    localHomeUrl() {
      return this.hrefOverride
        ? this.hrefOverride
        : this.homeUrl || util.getLocaleUrl() + "home.html";
    },
    localNavigation() {
      return this.navigation || this.navigationFromService;
    },
    localRegion() {
      return this.region || this.regionFromService;
    },
    regionDisplay() {
      return this.getRegionDisplay(this.localRegion);
    },
    showFooterNav() {
      return (
        this.showMainNavigation &&
        !!this.footerNavigation &&
        this.footerNavigation.length > 0
      );
    },
    showMainNavigation() {
      return this.navigationStack.length === 0;
    },
    showSubNavigation() {
      return (
        !this.activeNavigation.navContentType ||
        !SUBNAV_HIDDEN_CONTENT_TYPES.includes(
          this.activeNavigation.navContentType
        )
      );
    },
    showSeeAllLink() {
      let topNav = null;
      if (this.navigationStack && this.navigationStack.length === 2) {
        topNav = this.localNavigation[this.navigationStack[0]];
      }
      return (
        !this.showMainNavigation &&
        !!topNav &&
        this.hasTopnavComponent(topNav) &&
        !topNav.hideSeeAll
      );
    }
  },
  mounted() {
    this.currentPage = window.location.pathname;
    this.addListeners();
  },
  beforeDestroy() {
    this.removeListeners();
  },
  methods: {
    init() {
      this.setCommonStrings();

      // Get and set navigation from service if not passed from parent
      if (!this.navigation) {
        this.setNavigationFromService();
      }

      // Get and set regions from service if not passed from parent.
      if (!this.region) {
        this.setRegionFromService();
      }
    },

    signOut() {
      AccountManager.logout();
      window.location.reload();
    },

    signIn() {
      events.publish(events.authentication.REQUEST_FOR_LOGIN, {
        href: window.location.href
      });
    },

    signUp() {
      window.location.href = this.signupHref;
    },

    /**
     * See if the string is a JSON string.
     */
    fromJsonString(string, defaultValue = undefined) {
      try {
        return JSON.parse(string);
      } catch (error) {
        return string || defaultValue;
      }
    },

    async setNavigationFromService() {
      const data = await TopNavService.getTopNavigation();
      this.navigationFromService = data;
    },

    async setRegionFromService() {
      const data = await MarketSiteRegions.getMarketSiteRegionByCountry(
        this.runConfig.country
      );

      this.regionFromService =
        data.countryname.find(
          co => co.countryCode === this.runConfig.country
        ) ||
        // else find default
        data.countryname.find(co => co.countryCode === "US");
    },

    async setCommonStrings() {
      const commonStrings = [
        { key: "allCategoryText", dflt: "See all %categoryName%" }
      ];

      const { allCategoryText } = await StringService.getStrings(commonStrings);

      this.allCategoryText = allCategoryText;
    },

    getRegionDisplay(region) {
      const currLanguageCode = RunConfigService.getRunConfig().language;

      let language = "";
      let countryName = "";
      let otherLanguages = [];

      // Get the current language
      if (region.languageCodes) {
        const languageIndex = region.languageCodes.findIndex(
          code => code === currLanguageCode
        );
        language = (region.languages || [])[languageIndex];
      }

      // Get the current country in local language
      if (region.locale_names) {
        countryName = region.locale_names.find(
          loc => Object.keys(loc)[0] === currLanguageCode
        )[currLanguageCode];
      }

      // Get "other languages" for dropdown. ({language, path, active, show})
      if (region.languages) {
        otherLanguages = region.languageCodes.map((languageCode, index) => {
          const language = region.languages[index];
          const path = region.paths.find(
            item => Object.keys(item)[0] === languageCode
          )[languageCode];

          return {
            language,
            path: this.getPathFromLocale(path),
            active: languageCode === currLanguageCode,
            show: this.regionsAllowed.length
              ? this.regionsAllowed.includes(
                  languageCode + "_" + region.countryCode
                )
              : true
          };
        });
      }

      return {
        countryCode: region.countryCode,
        countryName,
        language,
        otherLanguages: otherLanguages.filter(lang => lang.show)
      };
    },

    getPathFromLocale(localePath) {
      const currentURL = window.location.pathname;
      const fullPath =
        localePath +
        "/" +
        currentURL.substr(util.getLocaleUrl().length, currentURL.length);
      return fullPath;
    },

    hasTopnavComponent(navItem) {
      return (
        !!navItem.navContentType &&
        TOPNAV_CONTENT_TYPES.includes(navItem.navContentType)
      );
    },

    allowSubNavigation(navItem) {
      return (
        (navItem.subnav && navItem.subnav.length > 0) ||
        this.hasTopnavComponent(navItem)
      );
    },

    selectSubNavigation(navIndex) {
      this.navigationStack.push(navIndex);
      this.setActiveNavigationCustomComponent();
    },

    handleBackNavigation() {
      this.navigationStack.pop();
      this.setActiveNavigationCustomComponent();
    },

    resetNavigation() {
      this.navigationStack = [];
      this.activeNavigationCustomComponent = "";
    },

    toggleRegionDropdown() {
      this.regionSelectorOpen = !this.regionSelectorOpen;
    },

    selectOtherRegion() {
      if (this.regionsAllowed && this.regionsAllowed.length) {
        SitesSessionStorageService.writeItem(
          REGIONS_ALLOWED_KEY,
          JSON.stringify(this.regionsAllowed)
        );
      }

      if (this.hrefOverride) {
        SitesSessionStorageService.writeItem(
          HREF_OVERRIDE_KEY,
          isString(this.hrefOverride)
            ? this.hrefOverride
            : JSON.stringify(this.localHomeUrl)
        );
      }

      SitesLocalStorageService.writeItem(
        CURRENT_PAGE_KEY,
        window.location.href
      );

      SitesLocalStorageService.removeItem(AUTO_REDIRECT_KEY);

      window.location = this.regionSelectorPath;
    },

    getActiveNavigation(navItem, stackIndex = 0) {
      const navIndex = this.navigationStack[stackIndex];
      if (navIndex >= 0) {
        const subnavItem = navItem.subnav[navIndex];
        if (subnavItem) {
          const active = this.getActiveNavigation(subnavItem, stackIndex + 1);
          if (navItem.hideSeeAll) {
            active.hideSeeAll = navItem.hideSeeAll;
          }
          return active;
        }
      }
      return navItem;
    },

    setActiveNavigationCustomComponent() {
      this.activeNavigationCustomComponent = "";
      if (
        this.hasTopnavComponent(this.activeNavigation) &&
        this.activeNavigation.navContentUrl
      ) {
        const _this = this;
        axios
          .get(this.activeNavigation.navContentUrl)
          .then(function(response) {
            _this.activeNavigationCustomComponent = response.data.trim();
          })
          .catch(function(error) {
            console.error("Failed to get Custom Navigation content.", error);
          });
      }
    },

    handleCloseClick() {
      this.resetNavigation();
      this.$emit("close-drawer");
    },

    handleFooterSignupClick() {
      window.location.href = this.signupHref;
    },

    /**
     * Subscribe to events, should only be called when mounted
     */
    addListeners() {
      // watch app service to finish loading
      this.$watch(
        function() {
          return !this.$NsAppService.loading;
        },
        this.init,
        {
          immediate: true
        }
      );

      // listen to config changes
      vueEventBus.$on("config-changed", this.init);
    },

    /**
     * Unsubscribe from events, should only be called when destroyed
     */
    removeListeners() {
      // stop listening to event bus
      vueEventBus.$off();
    }
  }
};
</script>

<style lang="scss" module>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/global.mixins";
@use "~@nuskin/ns-core-styles/src/scss/globalVariables";
@use "~@nuskin/ns-core-styles/src/scss/colors";
@use "~@nuskin/ns-core-styles/src/scss/other";
@use "~@nuskin/ns-core-styles/src/scss/typographyVariables";

.container,
.container * {
  box-sizing: border-box;
  -webkit-font-smoothing: antialiased;
  user-select: none;
  font-family: map.get(typographyVariables.$typography, font-family-primary);
}

.container {
  margin: 0 auto;
  display: inline-block;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  background-color: #fff;
}

/* ****** */
/* HEADER */
/* ****** */

.drawerHead {
  position: relative;
  display: flex;
  height: 60px;
  width: 100%;
  font-family: map.get(typographyVariables.$typography, font-family-primary);
  border-radius: 0px;
  box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.15);
  margin-bottom: 27px;
}

.backButton {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 60px;
  width: 60px;
  cursor: pointer;

  // nested for specificity
  .closeIcon {
    width: 14px;
    height: 14px;
    color: map.get(colors.$themeColors, color-interactive-9);
  }
}

.drawerTitle {
  flex: 1;
  height: 60px;
  line-height: 60px;
  padding-right: 20px;
  color: map.get(colors.$themeColors, color-interactive-7);
  text-align: right;
  font-size: 16px;
  font-weight: map.get(typographyVariables.$typography, font-weight-semibold);
  letter-spacing: 0.8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.closeButton {
  display: flex;
  align-items: center;
  justify-content: center;
  border-left: 1px solid rgba(0, 0, 0, 0.15);
  width: 60px;
  height: 60px;
  cursor: pointer;

  // nested for specificity
  .closeIcon {
    width: 14px;
    height: 14px;
    color: map.get(colors.$themeColors, color-interactive-7);
  }
}

/* ********** */
/* NAVIGATION */
/* ********** */

a.navItem {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  min-height: 45px;
  padding: 10px 15px 10px 30px;
  color: map.get(colors.$themeColors, color-interactive-7);
  text-decoration: none !important;
  cursor: pointer;
  border-bottom: 1px solid rgba(0, 0, 0, 0.15);
}

a.navItem:focus {
  outline: none;
}

a.navItem:visited,
a.navItem:active,
a.navItem:hover {
  color: map.get(colors.$themeColors, color-interactive-7);
}

.navTitle {
  line-height: normal;
  font-size: 18px;
  font-weight: normal;
  letter-spacing: 0.8px;
}

.navIcon {
  flex-shrink: 0;
  transform: rotate(90deg);
}

.secondaryActions {
  margin-top: 25px;
}

.regionSelector {
  margin-left: 20px;
  margin-bottom: 20px;
}
.region {
  position: relative;
  width: 100%;
  cursor: pointer;
}

.regionToggle {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  min-height: 45px;
  padding: 10px 15px 10px 30px;
  color: map.get(colors.$themeColors, color-interactive-8);
  font-size: 18px;
  font-weight: 300;
  line-height: normal;

  span:empty {
    display: none;
  }
}

.regionCountry {
  margin-right: 2px;
}

.regionLanguage {
  position: relative;
  padding-left: 11px;
  text-transform: capitalize;

  &::before {
    content: "";
    height: 16px;
    width: 1px;
    position: absolute;
    left: 5px;
    top: 0;
    bottom: 0;
    margin: auto;
    background-color: map.get(colors.$themeColors, color-interactive-8);
  }
}

// nested for specificity
.regionToggle .downIcon {
  width: 14px;
  height: 14px;

  &.open {
    transform: rotate(180deg);
  }
}

.regionDropdown {
  width: 100%;
  z-index: map.get(other.$otherUtils, z-dropdown);
}

a.regionOption {
  @include global.p-medium;
  position: relative;
  padding: 10px 15px 10px 30px;
  display: flex;
  flex-direction: row;
  align-items: center;
  text-decoration: none !important;
  transition: map.get(other.$otherUtils, global-transition) !important;
  color: map.get(colors.$themeColors, color-interactive-7);

  &::before {
    @include global.hide;
    content: "";
    width: 5px;
    height: 32px;
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    margin: auto;
    background-color: map.get(colors.$themeColors, color-interactive-7);
  }

  &.active {
    font-weight: map.get(typographyVariables.$typography, font-weight-semibold);
    color: map.get(colors.$themeColors, color-interactive-7);

    &::before {
      @include global.show;
    }
  }

  &.otherRegion {
    @include global.subtitle-small-bold-caps;
  }
}

a.regionOption:visited,
a.regionOption:active,
a.regionOption:hover {
  color: map.get(colors.$themeColors, color-interactive-7);
}

/* ********** */
/* FOOTER NAV */
/* ********** */

.footerNav {
  margin-top: 29px;
  display: flex;
  flex-direction: column;
  background-color: map.get(colors.$themeColors, color-interactive-8);

  .footerNavHead {
    background-color: map.get(colors.$themeColors, color-interactive-9);
    padding: 15px;
  }

  .footerNavBody {
    padding: 15px;
  }
}

.signOutLink {
  background-color: #f5f5f5;
  padding-left: 25px;
  padding-top: 5px;
  height: 30px;
  font-weight: 700;

  span {
    cursor: pointer;
  }

  span.slash {
    cursor: auto;
  }
}

.slash {
  margin-left: 10px;
  margin-right: 10px;
}
</style>
